import React from 'react'
import { Card, Typography, Skeleton, Collapse, Space } from 'antd'
import Editor from '../../../../../../components/common/Editor/Editor'
import { useSelector } from 'react-redux'
import { rubricRefinementSelectors } from '../../../../../../store/selectors/rubricRefinement.selectors'
import { getCategoryById, getQuestionById } from '../../../../../../store/apis/utils/selectors/question.api.selectors'
import { useThemeColors } from '../../../../../../hooks/useThemeColors'
import { isNumber } from 'lodash'
import { getColorByIndex } from '../../../../../../utils/colors.helper'
import { useParams } from 'react-router'
import { useVersionId } from '../../../../../../utils/parseURL.helper'
import { useFetchQuestionsQuery } from '../../../../../../store/apis/question.api'
import { useMemo } from 'react'

const SubmissionCard = ({ isLoading: isLoadingProps }) => {
    const colors = useThemeColors()
    const displayedSubmissionItem = useSelector(rubricRefinementSelectors.displayedSubmissionItem)
    const hoveredCategoryId = useSelector(rubricRefinementSelectors.hoveredCategoryId)
    const hasStarted = useSelector(rubricRefinementSelectors.hasStarted)
    const refinementProcess = useSelector(rubricRefinementSelectors.refinementProcess)

    const { token, layerId } = useParams()
    const versionId = useVersionId()
    const { data: questions, isFetching: isQuestionsFetching } = useFetchQuestionsQuery({ token, versionId })
    const isLoading = isLoadingProps || isQuestionsFetching

    const dependentQuestion = useMemo(() => {
        if (!questions || !layerId) return null
        const currentQuestion = getQuestionById(questions, Number(layerId))
        const dependentId = currentQuestion?.dependsOnQuestionIds?.[0]
        if (!dependentId) return null
        return getQuestionById(questions, Number(dependentId))
    }, [questions, layerId])

    // If we haven't started yet, don't render anything
    if (!hasStarted || !displayedSubmissionItem) return null

    // If we're initializing, don't render anything
    if (refinementProcess?.status === 'Initializing') return null

    const getHighlightRanges = () => {
        const category = displayedSubmissionItem?.categories.find(c => c.id == hoveredCategoryId)

        if (!hoveredCategoryId || !questions || !category?.explainability?.highlights) return []

        const categoryDetails = getCategoryById(questions, hoveredCategoryId)

        return category.explainability.highlights.map(highlight => ({
            start: highlight.start,
            end: highlight.end,
            opacity: highlight.importance,
            color: isNumber(categoryDetails?.colorId) ? getColorByIndex(categoryDetails.colorId).value : colors.primary,
        }))
    }

    return (
        <Card className='submission-card'>
            {isLoading ? (
                <Skeleton active paragraph={{ rows: 6 }} title={false} />
            ) : (
                <Space direction='vertical' size='large'>
                    <Editor
                        language='text'
                        readOnly={true}
                        value={displayedSubmissionItem?.submission?.content}
                        showLineNumbers={false}
                        wrapLines
                        title={<Typography.Text type='secondary'>Submission</Typography.Text>}
                        highlightRanges={getHighlightRanges()}
                        selectedPatterns={[]}
                    />
                    {displayedSubmissionItem?.submission?.dependentContent ? (
                        <Collapse
                            ghost
                            bordered={false}
                            defaultActiveKey={['dependent']}
                            size='small'
                            items={[{
                                key: 'dependent',
                                label: <Typography.Text type='secondary'>Dependent question: {dependentQuestion?.name}</Typography.Text>,
                                styles: {
                                    body:{
                                        padding:0
                                    },
                                    header:{
                                        padding: 0
                                    },
                                },
                                children: (
                                    <Editor
                                        language='text'
                                        readOnly={true}
                                        value={displayedSubmissionItem?.submission?.dependentContent || 'No previous answer'}
                                        showLineNumbers={false}
                                        wrapLines
                                        highlightRanges={[]}
                                        selectedPatterns={[]}
                                        showActions={false}
                                    />
                                ),
                            }]}
                        />
                    ) : null}
                </Space>
            )}
        </Card>
    )
}

export default SubmissionCard
